/**
 * Trade Mint Dashboard Overlay
 * MT4-faithful design: signal-themed card at top-left.
 * Toggle support, arrow badges, status highlighting.
 * CSS prefix: tmint-
 */
import React from "react";
import { TradeMintDashboard } from "../../indicators/built-in/trade-mint";
import "./TradeMintOverlay.scss";

interface TradeMintOverlayProps {
  dashboard: TradeMintDashboard | null;
  symbol: string;
  visible: boolean;
  showDashboard?: boolean;
  onToggleDashboard?: () => void;
  arrowBadges?: { x: number; y: number; direction: "long" | "short" }[];
}

const TradeMintOverlay: React.FC<TradeMintOverlayProps> = ({
  dashboard, symbol, visible,
  showDashboard = true, onToggleDashboard, arrowBadges = [],
}) => {
  if (!visible || !dashboard) return null;

  const {
    signal, entry, target, stopLoss, currentPrice, status,
    bookedPL, maxPoints, trend,
  } = dashboard;

  const isBuy = signal === "BUY";
  const isSell = signal === "SELL";
  const themeClass = isBuy ? "tmint-buy" : isSell ? "tmint-sell" : "tmint-neutral";
  const fmt = (v: number | null) => (v !== null ? v.toFixed(2) : "--");

  const trendArrow = trend === "Up Trend" ? "↑" : trend === "Down Trend" ? "↓" : "◆";
  const trendLabel = trend === "Up Trend" ? "Up Trend" : trend === "Down Trend" ? "Down Trend" : "Sideways";

  // Status row CSS class
  const statusClass =
    status === "Buy Active" ? "tmint-status-buy" :
    status === "Sell Active" ? "tmint-status-sell" :
    status === "Target Hit" ? "tmint-status-tp" :
    status === "Stop Loss Hit" ? "tmint-status-sl" : "";

  // Signal row CSS class
  const signalClass =
    signal === "BUY" ? "tmint-signal-buy" :
    signal === "SELL" ? "tmint-signal-sell" : "";

  const rows: { label: string; value: string; cssClass?: string }[] = [
    { label: "Signal", value: signal ?? "Waiting", cssClass: signalClass },
    { label: "Entry Price", value: fmt(entry) },
    { label: "Target", value: fmt(target) },
    { label: "Stop Loss", value: fmt(stopLoss) },
    { label: "Status", value: status, cssClass: statusClass },
  ];

  return (
    <>
      {/* Arrow Badges */}
      {arrowBadges.map((badge, i) => (
        <div
          key={i}
          className={`tmint-arrow-badge tmint-arrow-badge--${badge.direction}`}
          style={{
            left: badge.x,
            top: badge.direction === "long" ? badge.y + 30 : badge.y - 44,
          }}
        >
          {badge.direction === "long" ? "Buy" : "Sell"}
        </div>
      ))}

      <div className={`tmint-overlay ${themeClass}${showDashboard ? "" : " tmint-overlay--collapsed"}`}>
        {showDashboard && (
          <>
            {/* Header: Symbol row + Price row + Toggle */}
            <div className="tmint-header">
              <div className="tmint-header-row">
                <span className="tmint-symbol">{symbol}</span>
                <button
                  className="tmint-toggle-btn"
                  onClick={onToggleDashboard}
                  title="Hide dashboard"
                  aria-label="Hide dashboard"
                >
                  <span className="tmint-toggle-icon">&#x25B2;</span>
                </button>
              </div>
              <span className="tmint-cmp">{currentPrice.toFixed(2)}</span>
            </div>

            {/* Data Rows */}
            <div className="tmint-rows">
              {rows.map((r, i) => (
                <div key={i} className="tmint-row">
                  <span className="tmint-row-label">{r.label}</span>
                  <span className={`tmint-row-value ${r.cssClass ?? ""}`}>{r.value}</span>
                </div>
              ))}
            </div>

            {/* P&L boxes */}
            <div className="tmint-pl-row">
              <div className="tmint-pl-box">
                <span className="tmint-pl-label">Booked P&amp;L</span>
                <span className="tmint-pl-value">{bookedPL.toFixed(2)}</span>
              </div>
              <div className="tmint-pl-box">
                <span className="tmint-pl-label">Max. Points</span>
                <span className="tmint-pl-value">{maxPoints.toFixed(2)}</span>
              </div>
            </div>

            {/* Trend + Brand */}
            <div className="tmint-footer">
              <div className={`tmint-trend-pill ${trend === "Up Trend" ? "tmint-trend-up" : trend === "Down Trend" ? "tmint-trend-down" : "tmint-trend-side"}`}>
                <span className="tmint-trend-text">{trendLabel}</span>
                <span className="tmint-trend-arrow">{trendArrow}</span>
              </div>
              <div className="tmint-brand">Trade Mint</div>
            </div>
          </>
        )}

        {!showDashboard && (
          <>
            <div className="tmint-collapsed-title">Trade Mint</div>
            <button
              className="tmint-toggle-btn"
              onClick={onToggleDashboard}
              title="Show dashboard"
              aria-label="Show dashboard"
            >
              <span className="tmint-toggle-icon">&#x25BC;</span>
            </button>
          </>
        )}
      </div>
    </>
  );
};

export default TradeMintOverlay;
